import React, { useState, useEffect, useRef, useMemo } from 'react';
import './StatusLine.css';

// ── Status Messages ─────────────────────────────────────────
// Each operation type has a pool of messages that rotate.
// Inspired by Claude Code's "Discombobulating..." vibe.

const STATUS_POOLS = {
  generating: [
    'Mapping the territory...',
    'Growing the thinking tree...',
    'Branching out...',
    'Crystallizing structure...',
    'Weaving connections...',
    'Unfolding dimensions...',
    'Planting seeds...',
    'Charting pathways...',
    'Building the lattice...',
    'Sprouting insights...',
  ],
  regenerating: [
    'Reimagining from scratch...',
    'Reshuffling the deck...',
    'Fresh perspective loading...',
    'Rethinking everything...',
    'Starting over, but smarter...',
  ],
  debating: [
    'Devils advocating...',
    'Arguing both sides...',
    'Poking holes...',
    'Stress testing assumptions...',
    'Constructive disagreement...',
    'Battle of ideas...',
    'Finding the weak spots...',
  ],
  refining: [
    'Sharpening the edges...',
    'Strengthening foundations...',
    'Polishing the rough spots...',
    'Tightening the logic...',
    'Raising the bar...',
    'Forging stronger arguments...',
    'Distilling the essence...',
  ],
  portfolio: [
    'Exploring alternatives...',
    'Generating parallel universes...',
    'What-if-ing...',
    'Divergent thinking...',
    'Multiplying possibilities...',
    'Forking the timeline...',
  ],
  drilling: [
    'Going deeper...',
    'Tunneling down...',
    'Zooming in...',
    'Excavating detail...',
    'Unpeeling layers...',
  ],
  chatting: [
    'Thinking...',
    'Reasoning through this...',
    'Connecting the dots...',
    'Synthesizing...',
    'Working on it...',
    'Mulling it over...',
    'Processing...',
  ],
  learning: [
    'Building understanding...',
    'Crafting explanations...',
    'Preparing your lesson...',
    'Socratic method engaged...',
    'Knowledge synthesis...',
  ],
  prototype: [
    'Building the prototype...',
    'Wiring up screens...',
    'Crafting the UI...',
    'Assembling components...',
    'Making it interactive...',
  ],
  pattern: [
    'Executing thinking pattern...',
    'Running the pipeline...',
    'Multi-stage analysis...',
    'Pattern recognition...',
    'Processing stages...',
  ],
  scoring: [
    'Evaluating quality...',
    'Running the rubric...',
    'Measuring dimensions...',
    'Calibrating scores...',
  ],
  exporting: [
    'Crafting your document...',
    'Assembling slides...',
    'Formatting for export...',
    'Packaging deliverables...',
  ],
};

function pickRandom(pool) {
  return pool[Math.floor(Math.random() * pool.length)];
}

// ── StatusLine Component ─────────────────────────────────────

export default function StatusLine({
  isGenerating,
  isRegenerating,
  isDebating,
  isRefining,
  isPortfolio,
  isDrilling,
  isChatting,
  isLearning,
  isPrototype,
  isPattern,
  isScoring,
  isExporting,
}) {
  const [message, setMessage] = useState('');
  const [visible, setVisible] = useState(false);
  const intervalRef = useRef(null);
  const prevActiveRef = useRef(null);

  // Determine current active operation (priority order)
  const activeOp = useMemo(() => {
    if (isDebating) return 'debating';
    if (isRefining) return 'refining';
    if (isPortfolio) return 'portfolio';
    if (isPattern) return 'pattern';
    if (isPrototype) return 'prototype';
    // Learn mode has its own per-card status indicators (teach/probe/feedback).
    // Suppress the global rotating status pill — it duplicates info and adds noise.
    // if (isLearning) return 'learning';
    if (isExporting) return 'exporting';
    if (isScoring) return 'scoring';
    if (isDrilling) return 'drilling';
    if (isRegenerating) return 'regenerating';
    if (isGenerating) return 'generating';
    if (isChatting) return 'chatting';
    return null;
  }, [isGenerating, isRegenerating, isDebating, isRefining, isPortfolio,
      isDrilling, isChatting, isLearning, isPrototype, isPattern,
      isScoring, isExporting]);

  useEffect(() => {
    if (activeOp) {
      const pool = STATUS_POOLS[activeOp] || STATUS_POOLS.generating;

      // Pick a new message when operation type changes
      if (prevActiveRef.current !== activeOp) {
        setMessage(pickRandom(pool));
        prevActiveRef.current = activeOp;
      }

      setVisible(true);

      // Rotate messages every 4 seconds
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        setMessage(pickRandom(pool));
      }, 4000);
    } else {
      // Fade out
      setVisible(false);
      clearInterval(intervalRef.current);
      prevActiveRef.current = null;
    }

    return () => clearInterval(intervalRef.current);
  }, [activeOp]);

  if (!visible && !activeOp) return null;

  return (
    <div className={`status-line ${visible ? 'visible' : 'fading'}`}>
      <span className="status-line-icon">✦</span>
      <span className="status-line-text">{message}</span>
    </div>
  );
}
